import { ChevronDownIcon, ChevronUpIcon, DocumentTextIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { Collapse } from "@material-tailwind/react";
import { templateHasConclusionsFields } from "@utils/reportTemplateSections";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  ReportPageLocationEnums,
  ReportSectionsType,
  useCreateReportTemplateSectionsMutation,
} from "@api/graphql/types-and-hooks";
import { AiSourcesTooltipContent } from "@components/FieldComponent/FieldComponent";
import { ReportSection } from "@components/ReportField/ReportSection";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider";

interface DiagnosisBuilderSectionsProps {
  recommendationsSections: ReportSectionsType[];
  conclusionsSections: ReportSectionsType[];
  isReportCompleted: boolean;
  isSectionGenerating: boolean;
  collapseFields: { value: boolean };
}

export const DiagnosisBuilderSections: React.FC<DiagnosisBuilderSectionsProps> = ({
  recommendationsSections,
  conclusionsSections,
  isReportCompleted,
  isSectionGenerating,
  collapseFields,
}) => {
  const { reportId } = useParams();
  const { report, reportQuery } = useReport();

  // Template-driven, not org-driven: a report has zero *generated* Conclusions sections
  // both when nothing has been generated yet and when the template defines no Conclusions
  // fields at all. Only the template's own section definitions can tell those apart. See
  // PROA-1659.
  const hasConclusionsFields = templateHasConclusionsFields(report?.reportTemplate);

  const createReportTemplateSections = useCreateReportTemplateSectionsMutation({});
  const [isGeneratingConclusions, setIsGeneratingConclusions] = useState(false);

  const handleGenerateConclusions = async () => {
    setIsGeneratingConclusions(true);
    try {
      await createReportTemplateSections.mutateAsync({
        input: {
          reportId: reportId || "<missing-report-id>",
          pageLocation: ReportPageLocationEnums.Conclusions,
          useConverter: true,
        },
      });
      await reportQuery.refetch();
    } catch (err) {
      console.error("[DB Single Page] Failed to generate Conclusions:", err);
      notifyError("Failed to generate Summary section");
    } finally {
      setIsGeneratingConclusions(false);
    }
  };

  return (
    <>
      {/* Conclusions sections with Generate Summary button. Nothing renders when the
          template defines no Conclusions fields at all (e.g. ABC Kuwait) -- there is
          nothing to generate and no placeholder to offer. */}
      {hasConclusionsFields &&
        (conclusionsSections.length === 0 && !isReportCompleted ? (
          <ConclusionsPlaceholderSection
            isSectionGenerating={isSectionGenerating}
            isGeneratingConclusions={isGeneratingConclusions || createReportTemplateSections.isPending}
            onGenerateConclusions={handleGenerateConclusions}
          />
        ) : (
          conclusionsSections.map((section) => (
            <ReportSection
              key={section.id}
              section={section}
              isCollapsed={collapseFields}
              disabled={isReportCompleted}
            />
          ))
        ))}

      {/* Recommendations sections */}
      {recommendationsSections.map((section) => (
        <ReportSection key={section.id} section={section} isCollapsed={collapseFields} disabled={isReportCompleted} />
      ))}
    </>
  );
};

interface ConclusionsPlaceholderSectionProps {
  isSectionGenerating: boolean;
  isGeneratingConclusions: boolean;
  onGenerateConclusions: () => Promise<void>;
}

const ConclusionsPlaceholderSection: React.FC<ConclusionsPlaceholderSectionProps> = ({
  isSectionGenerating,
  isGeneratingConclusions,
  onGenerateConclusions,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <section className="flex flex-col relative duration-0 transition-all outline-solid outline-1 outline-white px-8 pt-8 pb-4 mb-4 rounded-3xl bg-white overflow-visible">
      <div onClick={() => setIsCollapsed(!isCollapsed)} className="flex items-center justify-between cursor-pointer">
        <div className="h-4 w-4 mr-2 text-text-secondary">
          <ChevronDownIcon className={`${!isCollapsed && "hidden"}`} />
          <ChevronUpIcon className={`${isCollapsed && "hidden"}`} />
        </div>
        <div className="flex items-center relative group w-full">
          <span className="border p-2 rounded-sm mr-2">
            <DocumentTextIcon className="h-6 w-6" />
          </span>
          <Paragraph displayType="loud" colorType="primary">
            Conclusions
          </Paragraph>
        </div>
      </div>

      <Collapse open={!isCollapsed}>
        <div className="overflow-visible py-4">
          {/* Summary field label */}
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <Paragraph displayType="loud" colorType="primary">
                Summary
              </Paragraph>
              <Tooltip
                text={
                  <AiSourcesTooltipContent
                    sources="Assessment Summaries, Interviews, Diagnostic Impressions"
                    useListIcon
                  />
                }
                placement="right"
                showInfoIcon={false}
                className="inline-block"
              >
                <span className="inline-flex max-h-6 min-w-fit items-center rounded-md px-1.5 py-0.5 text-xs font-normal cursor-pointer bg-blurple-50 text-blurple-700 mr-[25px]">
                  AI
                </span>
              </Tooltip>
            </div>
          </div>

          {/* Generate Summary card */}
          <div className="flex flex-col items-center justify-center py-8 px-4 bg-limestone-50 rounded-lg border border-limestone-200">
            <Paragraph displayType="loud" colorType="primary" className="mb-2">
              Generate Summary
            </Paragraph>
            <Paragraph displayType="normal" colorType="secondary" className="text-center mb-4">
              This section summarizes the full report. For best results, complete all other sections first, then click
              'Generate'.
            </Paragraph>
            {isSectionGenerating ? (
              <Tooltip text="All sections must finish generating before creating the summary" showInfoIcon={false}>
                <Button
                  text={
                    <div className="flex items-center">
                      <SparklesIcon className="mr-2 h-4 w-4" />
                      Generate
                    </div>
                  }
                  appearance="primary"
                  onClick={onGenerateConclusions}
                  disabled={true}
                />
              </Tooltip>
            ) : (
              <Button
                text={
                  <div className="flex items-center">
                    <SparklesIcon className="mr-2 h-4 w-4" />
                    {isGeneratingConclusions ? "Generating..." : "Generate"}
                  </div>
                }
                appearance="primary"
                onClick={onGenerateConclusions}
                disabled={isGeneratingConclusions}
              />
            )}
          </div>
        </div>
      </Collapse>
    </section>
  );
};
